import { useTranslation } from "react-i18next";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useState } from "react";
import { Users, Search, Filter, UserX, Facebook } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { formatPrice } from "@/utils/currency";

import { useCustomers } from "@/hooks/useCustomers";
import { StatsCards } from "@/components/customers/StatsCards";
import { CustomerTable } from "@/components/customers/CustomerTable";
import { Pagination } from "@/components/customers/Pagination";
import { CustomerDetail } from "@/components/customers/CustomerDetail";
import { InactiveCustomersTab } from "@/components/customers/InactiveCustomersTab";
import { CustomerIntelligenceTab } from "@/components/customers/CustomerIntelligenceTab";
import { SOURCE_LABELS } from "@/components/customers/customersUtils";
import { ExportDropdown } from "@/components/ui/ExportDropdown";

export default function CustomersPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("todos");
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  const {
    customers, stats, loading,
    search, setSearch,
    sortBy, sortDir, handleSort,
    sourceFilter, handleSourceFilter,
    page, setPage,
    totalPages, totalCount,
    fetchCustomers, fetchStats,
  } = useCustomers();

  // Build export rows from the currently loaded customers
  const exportHeaders = ["Nome", "Telefone", "Pedidos", "Total Gasto", "Ultimo Pedido", "Canal"];
  const exportRows = customers.map(c => [
    c.name || "",
    c.phone || "",
    String(c.total_orders ?? 0),
    formatPrice(c.total_spent ?? 0),
    c.last_order_date ? new Date(c.last_order_date).toLocaleDateString("pt-PT") : "",
    SOURCE_LABELS[c.source] || c.source || "",
  ]);

  // Facebook Custom Audience CSV (special format from backend — all customers)
  const handleFacebookExport = async () => {
    setFbLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/v1/customers/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `facebook_custom_audience_${new Date().toISOString().slice(0, 10)}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 200);
      toast.success("Ficheiro Facebook Ads pronto para upload");
    } catch {
      toast.error(t("errors.generic"));
    }
    setFbLoading(false);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="customers-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="customers-title">{t("admin.customersPage")}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t("admin.customersCRM")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Facebook Ads export — special format for Custom Audience upload */}
            <Button
              variant="outline"
              className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
              onClick={handleFacebookExport}
              disabled={fbLoading}
              data-testid="export-facebook-btn"
              title="Descarregar lista de clientes no formato Facebook Custom Audience"
            >
              <Facebook className="w-4 h-4" />
              {fbLoading ? "A exportar..." : "Facebook Ads"}
            </Button>

            {/* Multi-format export (CSV / Excel / PDF) from visible list */}
            <ExportDropdown
              headers={exportHeaders}
              rows={exportRows}
              filename="clientes"
              title="Lista de Clientes"
              label={t("admin.exportReport")}
              data-testid="export-dropdown-btn"
            />
          </div>
        </div>

        {stats && <StatsCards stats={stats} formatPrice={formatPrice} />}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="customers-tabs">
            <TabsTrigger value="todos" data-testid="tab-todos">
              <Users className="w-4 h-4 mr-1.5" /> {t("common.all")}
            </TabsTrigger>
            <TabsTrigger value="inativos" data-testid="tab-inativos">
              <UserX className="w-4 h-4 mr-1.5" /> Inactivos
            </TabsTrigger>
            <TabsTrigger value="intelligence" data-testid="tab-intelligence">
              <Filter className="w-4 h-4 mr-1.5" /> CRM 360°
            </TabsTrigger>
          </TabsList>

          <TabsContent value="todos" className="mt-4 space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3" data-testid="customers-filters">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre o teléfono..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="customer-search-input"
                />
              </div>
              <Select value={sourceFilter || "all"} onValueChange={handleSourceFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="source-filter">
                  <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Todos los canales" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los canales</SelectItem>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {loading ? <LoadingScreen /> : (
              <>
                <CustomerTable
                  customers={customers}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  onSelect={c => { setSelectedPhone(c.phone); setDetailOpen(true); }}
                  formatPrice={formatPrice}
                />
                <Pagination page={page} pages={totalPages} total={totalCount} onPageChange={setPage} />
              </>
            )}
          </TabsContent>

          <TabsContent value="inativos" className="mt-4">
            <InactiveCustomersTab />
          </TabsContent>
          <TabsContent value="intelligence" className="mt-4">
            <CustomerIntelligenceTab />
          </TabsContent>
        </Tabs>
      </div>

      <CustomerDetail
        phone={selectedPhone}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); fetchCustomers(); fetchStats(); }}
      />
    </MainLayout>
  );
}
